/**
 * Recursive snake_case → camelCase key conversion for API responses.
 *
 * Wired in as an axios response interceptor in `client.ts` so every
 * service consumes camelCase regardless of what the BE emits. Only
 * touches keys (values pass through untouched). Plain objects + arrays
 * are recursed; everything else (Date, Blob, etc.) returns as-is.
 *
 * Leading underscores (`_id`, `__proto__`) are preserved verbatim so
 * private/system keys round-trip unchanged. Internal `_+x` boundaries
 * are collapsed and the following alphanumeric is upper-cased, so
 * `address_1` → `address1` and `foo__bar` → `fooBar`.
 */
const INTERNAL_UNDERSCORES = /_+([a-z0-9])/g;
const LEADING_UNDERSCORES = /^_+/;

export const snakeToCamel = (key: string): string => {
  const leading = key.match(LEADING_UNDERSCORES)?.[0] ?? '';
  const body = key.slice(leading.length);
  return leading + body.replace(INTERNAL_UNDERSCORES, (_m, c: string) => c.toUpperCase());
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => (
  value !== null
  && typeof value === 'object'
  && Object.getPrototypeOf(value) === Object.prototype
);

export const camelizeKeys = <T = unknown>(input: T): T => {
  if (Array.isArray(input)) {
    return input.map((item) => camelizeKeys(item)) as unknown as T;
  }
  if (isPlainObject(input)) {
    return Object.keys(input).reduce<Record<string, unknown>>((acc, key) => {
      acc[snakeToCamel(key)] = camelizeKeys((input as Record<string, unknown>)[key]);
      return acc;
    }, {}) as T;
  }
  return input;
};
